import React, { useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { AlertCircle, CheckCircle2, Trash2 } from 'lucide-react';
import { functions } from '../../../firebase';

const STATUS_OPTIONS = [
  { value: 'empty', label: 'Vide', hint: 'File très fluide, aucune attente.' },
  { value: 'moderate', label: 'Moyen', hint: 'Affluence standard, quelques minutes.' },
  { value: 'full', label: 'Blindé', hint: 'Forte affluence, attente importante.' },
  { value: 'closed', label: 'Fermé', hint: 'Service interrompu ou RU fermé.' }
];

const DURATION_OPTIONS = [
  { ms: 30 * 60 * 1000, label: '30 min' },
  { ms: 60 * 60 * 1000, label: '1 h' },
  { ms: 2 * 60 * 60 * 1000, label: '2 h' },
  { ms: 6 * 60 * 60 * 1000, label: '6 h' },
  { ms: 24 * 60 * 60 * 1000, label: '24 h' }
];

function OverridePanel({ crousId, currentOverride }) {
  const [status, setStatus] = useState(currentOverride?.status || 'moderate');
  const [message, setMessage] = useState(currentOverride?.message || '');
  const [durationMs, setDurationMs] = useState(60 * 60 * 1000);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const isActive = !!(currentOverride && currentOverride.active);
  const expiresLabel = useMemo(() => {
    if (!isActive || !currentOverride.expiresAt) return null;
    const left = currentOverride.expiresAt - Date.now();
    if (left <= 0) return 'Expire à la prochaine lecture';
    const mins = Math.round(left / 60000);
    if (mins < 60) return `Expire dans ${mins} min`;
    const hrs = Math.floor(mins / 60);
    const rem = mins % 60;
    return rem ? `Expire dans ${hrs} h ${rem} min` : `Expire dans ${hrs} h`;
  }, [isActive, currentOverride]);

  const submit = async () => {
    setSubmitting(true);
    setFeedback(null);
    try {
      const fn = httpsCallable(functions, 'setCrousOverride');
      await fn({ crousId, status, message: message.trim(), durationMs });
      setFeedback({ kind: 'ok', text: 'Override appliqué. Les étudiants voient la mise à jour immédiatement.' });
    } catch (err) {
      setFeedback({ kind: 'err', text: err.message || "Échec de l'application." });
    } finally {
      setSubmitting(false);
    }
  };

  const clearOverride = async () => {
    setSubmitting(true);
    setFeedback(null);
    try {
      const fn = httpsCallable(functions, 'setCrousOverride');
      await fn({ crousId, clear: true });
      setFeedback({ kind: 'ok', text: 'Override levé.' });
    } catch (err) {
      setFeedback({ kind: 'err', text: err.message || "Échec." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="est-card">
      <div className="est-card-head">
        <div>
          <span className="est-kicker">Action directe</span>
          <h2>Surécriture du statut</h2>
          <p className="est-muted">
            Ce que tu choisis ici s'affiche immédiatement côté étudiants, au-dessus de
            l'affluence calculée par la communauté.
          </p>
        </div>
        {isActive && (
          <div className="est-badge est-badge-active">
            Override actif · {currentOverride.status}
            {expiresLabel && <small>{expiresLabel}</small>}
          </div>
        )}
      </div>

      <div className="est-override-grid">
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className={`est-override-option ${status === opt.value ? 'is-selected' : ''} est-override-${opt.value}`}
            onClick={() => setStatus(opt.value)}
          >
            <strong>{opt.label}</strong>
            <span>{opt.hint}</span>
          </button>
        ))}
      </div>

      <label className="est-field">
        <span>Message (optionnel, 280 caractères max)</span>
        <textarea
          maxLength={280}
          rows={2}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder='ex. "Plat du jour épuisé, service normal sur les menus alternatifs."'
        />
      </label>

      <div className="est-duration">
        <span className="est-muted">Durée</span>
        <div className="est-duration-chips">
          {DURATION_OPTIONS.map((o) => (
            <button
              key={o.ms}
              type="button"
              className={`est-chip ${durationMs === o.ms ? 'is-selected' : ''}`}
              onClick={() => setDurationMs(o.ms)}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {feedback && (
        <div className={`est-alert ${feedback.kind === 'ok' ? 'est-alert-ok' : 'est-alert-error'}`}>
          {feedback.kind === 'ok' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          <span>{feedback.text}</span>
        </div>
      )}

      <div className="est-actions">
        <button
          type="button"
          className="est-btn est-btn-primary"
          onClick={submit}
          disabled={submitting}
        >
          {submitting ? 'En cours…' : 'Appliquer'}
        </button>
        {isActive && (
          <button
            type="button"
            className="est-btn est-btn-ghost"
            onClick={clearOverride}
            disabled={submitting}
          >
            <Trash2 size={16} /> Lever l'override
          </button>
        )}
      </div>
    </div>
  );
}

export default OverridePanel;
